// ============================================================================
// role-gates — the ONE place the app names a sign-in role word.
//
// Every "who may see or do this" question the screens ask is answered here, by
// a helper named for the capability in a person's words. Each helper says which
// database rule decides the same thing for the data (the database is the
// authority; these only decide what to SHOW). When a role changes, it changes
// here, not across sixty files. `scripts/check-role-literals.mjs` refuses a
// role word compared anywhere else (outside this file and types.ts, which
// declares the role vocabulary itself).
//
// Where a helper does NOT agree with its database rule, its comment says
// "DISAGREES" and names the decision it is waiting on. It keeps what the screen
// did before (2026-10-20, "Role gates through one helper": no screen changes
// for anyone), so the decision becomes a one-line change here.
//
// Real holders, measured 2026-10-20: nobody signs in with the role word
// 'executive' (the executives are superusers, or sales with the Exec employee
// type), so a difference that only touches 'executive' changes nobody's screen.
// ============================================================================

import type { UserRole } from './types';

/** A role word, or anything carrying one (a profile, `{ role }`). */
export type RoleLike =
  | string
  | null
  | undefined
  | { role?: string | null; extra_roles?: readonly string[] | null };

function roleOf(r: RoleLike): string | null {
  if (r == null) return null;
  if (typeof r === 'string') return r;
  return r.role ?? null;
}

function hatsOf(r: RoleLike): readonly string[] {
  if (r == null || typeof r === 'string') return [];
  return r.extra_roles ?? [];
}

function isOneOf(r: RoleLike, seats: readonly string[]): boolean {
  const role = roleOf(r);
  return role !== null && seats.includes(role);
}

// ----------------------------------------------------------------------------
// The database's own seat tests, one for one.
// ----------------------------------------------------------------------------

/** is_superuser(). */
export const SUPERUSER_ONLY: UserRole[] = ['superuser'];
export function isSuperuser(r: RoleLike): boolean {
  return isOneOf(r, SUPERUSER_ONLY);
}

/** has_full_am_authority(): superuser, executive, national AM. */
export const FULL_AM_AUTHORITY: UserRole[] = ['superuser', 'executive', 'national_am'];
export function hasFullAmAuthority(r: RoleLike): boolean {
  return isOneOf(r, FULL_AM_AUTHORITY);
}

/** is_am_role(): account manager or national AM. */
export const AM_ROLES: UserRole[] = ['account_manager', 'national_am'];
export function isAmRole(r: RoleLike): boolean {
  return isOneOf(r, AM_ROLES);
}

/** has_finance_capability(): finance, executive, superuser — or the 'finance'
 *  extra-role hat (Antonela: HR primary + finance hat). */
export const FINANCE_CAPABLE: UserRole[] = ['finance', 'executive', 'superuser'];
export function hasFinanceCapability(profile: RoleLike): boolean {
  if (isOneOf(profile, FINANCE_CAPABLE)) return true;
  return hatsOf(profile).includes('finance');
}

/** Management-fee percentage (Ben, 2026-07-07): finance + superuser ONLY, plus
 *  the finance hat. Deliberately NARROWER than hasFinanceCapability() — no
 *  executive. Everyone else sees only "has a management fee", never the number. */
export function canViewManagementFee(profile: RoleLike): boolean {
  if (isOneOf(profile, ['finance', 'superuser'])) return true;
  return hatsOf(profile).includes('finance');
}

/** can_view_restricted_hr(): hr, executive, superuser, finance (payroll keys the
 *  bank details in — Jose). ⛔ PRIMARY ROLE ONLY: the finance hat is a
 *  finance-WRITE capability, and approving a payable is not the right to read a
 *  Social Security number. Never rebuild this on hasFinanceCapability(). */
export const RESTRICTED_HR_READERS: UserRole[] = ['hr', 'executive', 'superuser', 'finance'];
export function canViewRestrictedHr(profile: RoleLike): boolean {
  return isOneOf(profile, RESTRICTED_HR_READERS);
}

// ----------------------------------------------------------------------------
// One sign-in seat. For a screen's STARTING view (which queue, which filter,
// which tab opens first) — never for whether something may be done.
// ----------------------------------------------------------------------------

export const isFinanceSeat = (r: RoleLike): boolean => roleOf(r) === 'finance';
export const isHrSeat = (r: RoleLike): boolean => roleOf(r) === 'hr';
export const isSalesSeat = (r: RoleLike): boolean => roleOf(r) === 'sales';
export const isOpsSeat = (r: RoleLike): boolean => roleOf(r) === 'ops';
export const isExecutiveSeat = (r: RoleLike): boolean => roleOf(r) === 'executive';
export const isNationalAmSeat = (r: RoleLike): boolean => roleOf(r) === 'national_am';
/** The account-manager seat only — NOT the national AM (use isAmRole for both). */
export const isAccountManagerSeat = (r: RoleLike): boolean => roleOf(r) === 'account_manager';
/** Signed in but not yet given a role by an admin (auto-provisioned). */
export const isPendingSeat = (r: RoleLike): boolean => roleOf(r) === 'pending';
/** The role an unclassified sign-in is offered first on Admin → Users. */
export const FIRST_ROLE_FOR_A_NEW_SIGN_IN: UserRole = 'account_manager';

/** The roles an admin may give someone — provision_user()'s accepted list minus
 *  'pending' (the auto-provision state; assigning it is a no-op). The order is
 *  the picker's order on Admin → Users. */
export const ASSIGNABLE_ROLES: UserRole[] = [
  'superuser',
  'executive',
  'national_am',
  'account_manager',
  'ops',
  'hr',
  'finance',
  'sales',
];

/** The same roles in the order the Routing inbox's "route to a role" picker
 *  has always listed them (account managers first). */
export const ROUTABLE_ROLES: UserRole[] = [
  'account_manager',
  'national_am',
  'finance',
  'hr',
  'sales',
  'ops',
  'executive',
  'superuser',
];

/** The roles whose holders appear in the Routing inbox's person picker. */
export const ROUTE_TO_PERSON_ROLES: UserRole[] = [
  'account_manager',
  'national_am',
  'finance',
  'hr',
  'executive',
  'superuser',
];

/** routed_to_role token for "addressed to my role" (record_routing):
 *  finance → 'finance', hr → 'hr', either AM seat → 'am'. */
export function routingTokenFor(r: RoleLike): 'finance' | 'hr' | 'am' | null {
  if (isFinanceSeat(r)) return 'finance';
  if (isHrSeat(r)) return 'hr';
  if (isAmRole(r)) return 'am';
  return null;
}

/** A plain word for the seat a routed role belongs to (Send to… sheet). */
export function seatWord(r: RoleLike): string | null {
  if (isFinanceSeat(r)) return 'finance';
  if (isHrSeat(r)) return 'HR';
  if (isAmRole(r)) return 'account manager';
  return null;
}

/** Who a Home work row belongs to when it is handed off (finance or HR). */
export type OwningSeat = Extract<UserRole, 'finance' | 'hr'>;
export const FINANCE_SEAT: OwningSeat = 'finance';
export const HR_SEAT: OwningSeat = 'hr';

// ----------------------------------------------------------------------------
// Capabilities, by part of the app. "Mirrors" names the database rule that
// decides the same thing for the data.
// ----------------------------------------------------------------------------

// --- Menus (nav-config). A menu entry is tidiness, not access: the page's own
// data rules decide what loads. Where a menu hides a page the data rules let a
// seat read, the comment says so.

/** People Desk + People directory (Ben, 2026-10-07, item 818). The employees
 *  table also lets finance read every row; the Desk is HR's queue by his ruling. */
export const DESK_SEATS: UserRole[] = ['superuser', 'executive', 'hr'];
export const isDeskSeat = (r: RoleLike): boolean => isOneOf(r, DESK_SEATS);

/** Hiring and Onboarding menus. Hiring requests are readable by every employee;
 *  onboarding by AMs, HR, finance and executives. */
export const HIRING_MENU: UserRole[] = ['superuser', 'executive', 'hr', 'national_am', 'account_manager'];
/** Subcontractors menu. */
export const SUBCONTRACTORS_MENU: UserRole[] = [
  'superuser', 'executive', 'finance', 'hr', 'national_am', 'account_manager',
];
/** Invoicing menu. Mirrors who the invoices table lets read anything (finance-
 *  capable, AMs on their own buildings, HR on hourly invoices). */
export const INVOICING_MENU: UserRole[] = [
  'superuser', 'executive', 'finance', 'national_am', 'account_manager', 'hr',
];
/** Every office seat: Contracts, E-Sign, New Accounts, Bids. All assignable
 *  roles except 'ops' (which nobody holds). */
export const OFFICE_SEATS: UserRole[] = [
  'superuser', 'executive', 'national_am', 'account_manager', 'finance', 'hr', 'sales',
];
/** Projects & Billing Comms menu — every office seat; sales added by Ben,
 *  2026-10-21: "Sales helps by putting in project work" (projects are editable
 *  by every employee, TCSD-0170). */
export const PROJECTS_MENU: UserRole[] = [
  'sales',
  'superuser', 'executive', 'national_am', 'account_manager', 'finance', 'hr',
];
/** Time & Labor menu. Sales added by Ben, 2026-10-23 ("ANY TCS FULL EMPLOYEE CAN
 *  SEND FAs HOURS LINKS. Sales helps Ops."): sales sees the one Hours links tab. */
export const TIME_MENU: UserRole[] = [
  'superuser', 'executive', 'hr', 'finance', 'national_am', 'account_manager', 'sales',
];

/** The field seats: My Route, the field layout of the phone menu, the Route card
 *  on Home. has_full_am_authority() OR is_am_role(). The route tables
 *  themselves are readable by every employee. */
export const FIELD_SEATS: UserRole[] = ['superuser', 'executive', 'national_am', 'account_manager'];
export const isFieldSeat = (r: RoleLike): boolean => isOneOf(r, FIELD_SEATS);

// --- Money

/** Finance pages: the Recent Changes, Rate Proposals and Annual Increases
 *  MENUS. The data behind them is readable by every employee (TCSD-0524/0525);
 *  the menus stay finance's by Ben's choice, 2026-10-21: "every employee is
 *  able to access the data in the DB", not every module. */
export const FINANCE_PAGES: UserRole[] = ['superuser', 'executive', 'finance'];

/** A building's rate history: the link on the building card and the
 *  read-only Rate Proposals page it opens. Finance, executive, superuser, and —
 *  Ben, 2026-10-21 — sales and account managers, "so they can see when rates
 *  were last changed". The proposals are readable by every employee
 *  (tcs_employee_read_all); deciding one stays decider_act's (finance,
 *  executive, superuser), and no screen offers it. */
export const RATE_HISTORY_READERS: UserRole[] = [
  'finance', 'executive', 'superuser', 'national_am', 'account_manager', 'sales',
];
export const seesRateHistory = (r: RoleLike): boolean => isOneOf(r, RATE_HISTORY_READERS);

/** Validate or defer an annual increase. Mirrors validate_kicker() /
 *  defer_kicker() and kicker_validations' read rule. */
export const canWorkAnnualIncreases = (r: RoleLike): boolean => isOneOf(r, FINANCE_PAGES);

/** Show the payment records against a project line on the project card.
 *  partner_payments is readable by every employee since 2026-10-21
 *  (TCSD-0525); the card keeps showing them to finance, executive and
 *  superuser only — Ben, 2026-10-21, opened the data, not this panel. */
export const SUB_PAYMENT_READERS: UserRole[] = ['finance', 'executive', 'superuser'];
export const canReadSubPaymentRecords = (r: RoleLike): boolean => isOneOf(r, SUB_PAYMENT_READERS);

/** Incentive payouts (menu, dashboard card). Mirrors incentive_payouts' read
 *  rule (finance, executive, superuser, or an AM on the building) plus its
 *  update rule (full AM authority, AMs on their buildings). */
export const INCENTIVE_SEATS: UserRole[] = [
  'superuser', 'executive', 'national_am', 'finance', 'account_manager',
];
export const seesIncentives = (r: RoleLike): boolean => isOneOf(r, INCENTIVE_SEATS);

/** Mark a No-Miss payout paid. Mirrors trg_fn_incentive_payouts_status:
 *  finance, superuser, executive — a national AM may NOT. */
export const canMarkIncentivePaid = (r: RoleLike): boolean => isOneOf(r, FINANCE_PAGES);

// --- Invoices

/** Check the hours on an hourly invoice. Mirrors validate_invoice_hours():
 *  hr or superuser — HR checks the hours, finance approves (Ben, 2026-10-20,
 *  role-gates Decision 2, TCSD-0527). */
export const canValidateInvoiceHours = (r: RoleLike): boolean => isOneOf(r, ['hr', 'superuser']);

// --- Contracts

/** Start a contract. Mirrors contracts_insert_sales_am. */
export const CONTRACT_STARTERS: UserRole[] = [
  'sales', 'account_manager', 'national_am', 'executive', 'superuser',
];
export const canStartContract = (r: RoleLike): boolean => isOneOf(r, CONTRACT_STARTERS);

/** Author, send for signature and move a contract through signing. Mirrors
 *  esign_can_manage_envelope() and contracts_update_post_approval. */
export const CONTRACT_SENDERS: UserRole[] = ['superuser', 'executive', 'sales', 'national_am'];
export const canSendContracts = (r: RoleLike): boolean => isOneOf(r, CONTRACT_SENDERS);

/** Give the legal or executive approval on a contract. Mirrors
 *  approve_contract_legal() / approve_contract_executive(). */
export const canGiveExecutiveContractApproval = (r: RoleLike): boolean =>
  isOneOf(r, ['executive', 'superuser']);

/** Edit a contract's lines at any stage (a creator may while it is a Draft).
 *  Mirrors contract_line_items_write_by_owner. */
export const canEditContractLinesAnyTime = (r: RoleLike): boolean =>
  isOneOf(r, ['executive', 'superuser', 'finance']);

// --- Building stop-service (terminations)

/** Open or cancel a termination. Mirrors rpc_create_account_termination() and
 *  rpc_cancel_account_termination(). */
export const TERMINATION_OPENERS: UserRole[] = ['account_manager', 'national_am', 'executive', 'superuser'];
export const canOpenTermination = (r: RoleLike): boolean => isOneOf(r, TERMINATION_OPENERS);

/** Pause a building: any active employee (rpc_pause_facility: is_tcs_employee()),
 *  so only a sign-in still waiting for a role is left out. */
export const canPauseService = (r: RoleLike): boolean => roleOf(r) !== null && !isPendingSeat(r);

/** The finance acknowledgement on a termination. Mirrors
 *  rpc_ack_account_termination()'s finance step: has_finance_capability(), the
 *  finance hat included (role-gates Decision 3, TCSD-0527). Pass the profile. */
export const canAckTerminationForFinance = (r: RoleLike): boolean => hasFinanceCapability(r);

/** The HR acknowledgement. Mirrors rpc_ack_account_termination()'s HR step. */
export const canAckTerminationForHr = (r: RoleLike): boolean =>
  isOneOf(r, ['hr', 'executive', 'superuser']);

/** Final approval and the final date. Mirrors rpc_ack_account_termination()'s
 *  approve step and rpc_set_account_termination_final_date(). */
export const canGiveFinalTerminationApproval = (r: RoleLike): boolean =>
  isOneOf(r, ['executive', 'superuser']);

// --- HR

/** Hold the pen on an HR record (person card, directory edits, separations).
 *  Mirrors hr_assert_may_write(): hr or superuser. */
export const HR_WRITERS: UserRole[] = ['hr', 'superuser'];
export const canWriteHrRecord = (r: RoleLike): boolean => isOneOf(r, HR_WRITERS);

/** A sub's Direct Deposit document — sensitive. Mirrors partner_documents,
 *  where only hr (and superuser) read or write the Direct_Deposit row. ⛔ Keep
 *  narrower than canWriteOnboarding on purpose. */
export const canHandleDirectDeposit = (r: RoleLike): boolean => isOneOf(r, HR_WRITERS);

/** Edit onboarding records and a sub's non-sensitive documents. Mirrors
 *  employee_onboarding / partner_documents: hr_update + full_am_update +
 *  superuser_all. */
export const canWriteOnboarding = (r: RoleLike): boolean =>
  isOneOf(r, ['hr', 'superuser']) || hasFullAmAuthority(r);

/** A person's full date of birth on the card (others see month and day).
 *  Stricter than the database ON PURPOSE: finance may read the full date, and
 *  the card still shows Jose month and day (Ben, 2026-10-20, role-gates
 *  Decision 6, TCSD-0527). */
export const seesFullDateOfBirth = (r: RoleLike): boolean => isOneOf(r, DESK_SEATS);

/** Edit any hiring request (an AM edits their own). Mirrors hiring_requests:
 *  hr_update + full_am_update + superuser_all. */
export const canEditAnyHiringRequest = (r: RoleLike): boolean =>
  isOneOf(r, ['hr', 'superuser']) || hasFullAmAuthority(r);

/** The mine/all switch on Hiring. A screen choice; no database rule. */
export const seesHiringScopeSwitch = (r: RoleLike): boolean =>
  isOneOf(r, ['superuser', 'account_manager', 'hr', 'national_am']);

/** Hard-delete a record: the button shows to superuser and executive; everyone
 *  else archives. admin_force_delete() admits a trusted database caller or two
 *  named PEOPLE (EID-0015, EID-0002) rather than a position — noted, not
 *  changed (its trusted-caller test was fixed 2026-10-20, item #925). */
export const canHardDelete = (r: RoleLike): boolean => isOneOf(r, ['superuser', 'executive']);

// --- Time & Labour. The database's time rules read is_executive(), which also
// admits the Exec employee type (Zach, CRO, sign-in role sales). The app has
// only the sign-in role, so these say 'executive' where the database says "Exec
// type". Carrying the type to the app is TCSD-0522 option 2, deferred by Ben
// until the CRO needs it — not a new decision.

/** Generate pay-run exports and reports. Mirrors fn_time_export_gate():
 *  hr, finance, is_executive(), superuser. */
export const canRunPayAndReports = (r: RoleLike): boolean =>
  isOneOf(r, ['hr', 'finance', 'executive', 'superuser']);

/** Review the exception queue and correct a timecard. Mirrors the
 *  rpc_time_* staff checks: hr, executive, superuser. */
export const canReviewTime = (r: RoleLike): boolean => isOneOf(r, ['hr', 'executive', 'superuser']);

/** Time & Labour admin: HR's own settings. hr or superuser. */
export const canAdministerTime = (r: RoleLike): boolean => isOneOf(r, HR_WRITERS);

/** Assign workers to buildings and shifts. Mirrors worker_assignments'
 *  hr_write / hr_update: hr, is_executive(), superuser. */
export const canAssignWorkers = (r: RoleLike): boolean => isOneOf(r, ['hr', 'superuser', 'executive']);

/** See every night manager's building time (item #997). Mirrors the seat half
 *  of building_visits_read: is_superuser() OR is_executive(). The other half —
 *  the employee the position reports to — is a fact about the position, so the
 *  screen adds it. */
export const seesAllNightManagerBuildingTime = (r: RoleLike): boolean => isOneOf(r, ['superuser', 'executive']);

/** Change how a night manager's night is recorded (Night only / Night +
 *  buildings). Mirrors the seat half of rpc_time_set_nm_building_checkins:
 *  is_superuser(). The other half — the employee the position reports to — is
 *  added by the screen. */
export const setsAnyNightManagerRecording = (r: RoleLike): boolean => isSuperuser(r);

/** Night managers and client views start on everyone rather than "my
 *  buildings". A screen choice; the data rules decide what loads. */
export const startsOnEveryBuilding = (r: RoleLike): boolean =>
  isOneOf(r, ['superuser', 'executive', 'national_am', 'hr', 'finance']);

// --- Fleet & assets

/** Add, edit and cost vehicles and machines. Mirrors rpc_asset_save_machine()
 *  and the vehicles / floor_machines fleet-admin policies. */
export const FLEET_ADMINS: UserRole[] = ['superuser', 'executive', 'national_am', 'finance'];
export const isFleetAdmin = (r: RoleLike): boolean => isOneOf(r, FLEET_ADMINS);

/** Renumber a floor machine. Mirrors rpc_asset_renumber_machine(). */
export const canRenumberMachine = (r: RoleLike): boolean => isOneOf(r, ['superuser', 'executive']);

// --- Supply

/** Approve or reject a supply request. Mirrors approve_supply_request() /
 *  reject_supply_request() (an AM only on their own buildings). */
export const SUPPLY_APPROVERS: UserRole[] = FIELD_SEATS;
export const canApproveSupplyRequest = (r: RoleLike): boolean => isOneOf(r, SUPPLY_APPROVERS);

/** Edit a purchase order. Narrower than purchase_orders' update rule
 *  (is_app_employee(): every app user) — a screen choice. */
export const canEditPurchaseOrder = (r: RoleLike): boolean =>
  isOneOf(r, ['superuser', 'executive', 'finance', 'national_am', 'account_manager']);

/** The mine/all switch on Supply requests. A screen choice. */
export const seesSupplyScopeSwitch = (r: RoleLike): boolean =>
  isOneOf(r, ['superuser', 'account_manager']);

// --- Subcontractor replacement

/** Approve or edit a sub replacement. Mirrors sub_replacement_requests:
 *  srr_update (full AM authority) + srr_update_finance (finance). */
export const canApproveSubReplacement = (r: RoleLike): boolean =>
  hasFullAmAuthority(r) || isFinanceSeat(r);

/** The sub-replacement list starts on everyone (HR and finance added
 *  2026-05-28). A screen choice; every employee may read the requests. */
export const startsOnAllSubReplacements = (r: RoleLike): boolean =>
  canApproveSubReplacement(r) || isHrSeat(r);

// --- New accounts

/** Archive a new-account card at the end of onboarding. Mirrors
 *  nas_archive_card(): finance or superuser. */
export const canArchiveNewAccountCard = (r: RoleLike): boolean => isOneOf(r, ['superuser', 'finance']);

// --- Projects & billing comms

/** The project card's finance controls. The finance-capable plus the national
 *  AM. Narrower than the projects table (every employee edits, TCSD-0170). */
export function worksProjectFinance(profile: RoleLike): boolean {
  return hasFinanceCapability(profile) || isNationalAmSeat(profile);
}

/** "Send back to the AM" on a project card. Finance, executive, superuser
 *  (Ben, 2026-10-20, role-gates Decision 4). The projects table lets every
 *  employee write the stage (open_edit_all_employees, TCSD-0170): measured
 *  2026-10-21, Jose's send-back succeeds. The old note that finance was refused
 *  came from a rule that no longer exists. */
export const showsSendBackToAm = (r: RoleLike): boolean => isOneOf(r, FINANCE_CAPABLE);

/** Resubmit any project card, not only one on your own building. */
export const canResubmitAnyProject = (r: RoleLike): boolean => isOneOf(r, ['national_am', 'superuser']);

/** The project list starts on everyone's cards at Ready to Invoice (finance's
 *  queue; HR added 2026-05-28 for Antonela). A screen choice. */
export const startsOnReadyToInvoice = (r: RoleLike): boolean =>
  isOneOf(r, ['finance', 'superuser', 'executive', 'national_am', 'hr']);

// --- Incentives

/** Validate and mark given (AMs on their buildings) / approve and generate
 *  (full AM authority). Mirrors incentive_payouts' update and insert rules. */
export const canValidateIncentive = (r: RoleLike): boolean => hasFullAmAuthority(r) || isAmRole(r);

// --- Admin

/** The role an auto-provisioned sign-in carries until an admin classifies it. */
export const PENDING_ROLE: UserRole = 'pending';

/** Admin → Users: approve, change a role, disable. Superusers only — mirrors
 *  provision_user() / disable_user() (Ben, 2026-10-20, role-gates Decision 5:
 *  A). Their trusted-caller test was fixed the same day (item #925), so the
 *  database now refuses everyone else too. */
export const canManageUsers = (r: RoleLike): boolean => isSuperuser(r);
